import { useCallback, useRef, useState } from 'react'
import { getCatalogsByCompany, Catalog, CatalogPartner, CatalogProfessional, catalogBasePrice } from '@/lib/api/catalog/catalog-api'
import { useCatalogCategoryLabels } from '@/lib/hooks/use-service-label'
import { CreateDealItemData } from '@/lib/api/sales/deals-api'
import { useAuth } from '@/store/hooks'
import {
    MdAdd,
    MdDelete,
    MdCheckCircle,
    MdBusiness,
    MdPerson,
    MdStorefront,
} from 'react-icons/md'

// Serviço e mercadoria saem do MESMO catálogo desde a fatia 4 da unificação — a origem
// "Produto/Serviço", que buscava no cadastro paralelo, deixou de existir.
type ItemSource = 'catalog' | 'manual'
type PriceSource = 'own' | 'partner' | 'professional'

interface PriceOption {
    source: PriceSource
    label: string
    price: number
    id?: number
}

interface ItemState {
    data: CreateDealItemData
    source: ItemSource
    priceSource: PriceSource
    selectedCatalog?: Catalog
    availablePrices: PriceOption[]
}

interface DealItemEditorProps {
    items: CreateDealItemData[]
    onChange: (items: CreateDealItemData[]) => void
}

function formatBRL(cents: number): string {
    return `R$ ${(cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
}

function formatBRLFromReais(value: number): string {
    return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
}

export default function DealItemEditor({ items, onChange }: DealItemEditorProps) {
    const { user } = useAuth()
    const categoryLabels = useCatalogCategoryLabels()

    const [itemStates, setItemStates] = useState<Record<number, Partial<ItemState>>>({})
    const [searchByIndex, setSearchByIndex] = useState<Record<number, string>>({})
    const [resultsByIndex, setResultsByIndex] = useState<Record<number, any[]>>({})
    const [loadingByIndex, setLoadingByIndex] = useState<Record<number, boolean>>({})
    const searchTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({})

    const getState = (index: number): ItemState => ({
        data: items[index] || { name: '', unitPrice: 0 },
        source: itemStates[index]?.source || 'catalog',
        priceSource: itemStates[index]?.priceSource || 'own',
        selectedCatalog: itemStates[index]?.selectedCatalog,
        availablePrices: itemStates[index]?.availablePrices || [],
    })

    const updateItemData = (index: number, updates: Partial<CreateDealItemData>) => {
        const updated = [...items]
        updated[index] = { ...updated[index], ...updates }
        onChange(updated)
    }

    const updateState = (index: number, updates: Partial<ItemState>) => {
        setItemStates(prev => ({ ...prev, [index]: { ...prev[index], ...updates } }))
    }

    const addItem = () => {
        onChange([...items, { name: '', unitPrice: 0, quantity: 1 }])
    }

    const removeItem = (index: number) => {
        onChange(items.filter((_, i) => i !== index))
        setItemStates(prev => {
            const next = { ...prev }
            delete next[index]
            return next
        })
    }

    const handleSearch = useCallback((index: number, term: string) => {
        setSearchByIndex(prev => ({ ...prev, [index]: term }))
        if (searchTimers.current[index]) clearTimeout(searchTimers.current[index])
        if (!term.trim() || !user?.companyId) {
            setResultsByIndex(prev => ({ ...prev, [index]: [] }))
            return
        }
        searchTimers.current[index] = setTimeout(async () => {
            setLoadingByIndex(prev => ({ ...prev, [index]: true }))
            try {
                const source = getState(index).source
                if (source === 'catalog') {
                    const res = await getCatalogsByCompany(user.companyId!, { search: term, limit: 15, activeOnly: true })
                    if (res.success) setResultsByIndex(prev => ({ ...prev, [index]: res.result || [] }))
                }
            } finally {
                setLoadingByIndex(prev => ({ ...prev, [index]: false }))
            }
        }, 300)
    }, [user?.companyId, itemStates])

    const selectCatalog = (index: number, catalog: Catalog) => {
        // Montar opções de preço
        const prices: PriceOption[] = []

        // Preço próprio (da company selecionada)
        const ownPrice = catalogBasePrice(catalog, user?.companyId)
        prices.push({
            source: 'own',
            label: 'Próprio',
            price: ownPrice,
        })

        // Preços de parceiros
        if (catalog.partners?.length) {
            for (const p of catalog.partners) {
                if (!p.active) continue
                const pPrice = p.sellingPrice || p.price
                if (pPrice) {
                    prices.push({
                        source: 'partner',
                        label: p.partner?.name || `Parceiro #${p.partnerId}`,
                        price: pPrice,
                        id: p.partnerId,
                    })
                }
            }
        }

        // Preços de profissionais
        if (catalog.professionals?.length) {
            for (const p of catalog.professionals) {
                if (!p.active) continue
                const pPrice = p.sellingPrice || p.price
                if (pPrice) {
                    prices.push({
                        source: 'professional',
                        label: p.professional?.name || `Profissional #${p.professionalId}`,
                        price: pPrice,
                        id: p.professionalId,
                    })
                }
            }
        }

        // Selecionar o preço do parceiro do usuário automaticamente, senão o próprio
        const userPartnerId = (user as any)?.partnerId
        let selectedPrice = prices[0]
        if (userPartnerId) {
            const partnerPrice = prices.find(p => p.source === 'partner' && p.id === userPartnerId)
            if (partnerPrice) selectedPrice = partnerPrice
        }

        updateItemData(index, {
            name: catalog.name,
            unitPrice: selectedPrice.price,
            catalogId: catalog.id,
        })

        updateState(index, {
            selectedCatalog: catalog,
            availablePrices: prices,
            priceSource: selectedPrice.source,
        })

        setSearchByIndex(prev => ({ ...prev, [index]: '' }))
        setResultsByIndex(prev => ({ ...prev, [index]: [] }))
    }

    const changePriceSource = (index: number, option: PriceOption) => {
        updateItemData(index, { unitPrice: option.price })
        updateState(index, { priceSource: option.source })
    }

    return (
        <div className="space-y-4">
            {items.map((item, i) => {
                const state = getState(i)
                const search = searchByIndex[i] || ''
                const results = resultsByIndex[i] || []
                const searching = loadingByIndex[i] || false
                const isLinked = !!item.catalogId
                const total = ((item.quantity || 1) * item.unitPrice) - (item.discount || 0)

                return (
                    <div key={i} className="border border-base-300 rounded-xl p-4 space-y-3 bg-base-100">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-base-content/50">Item {i + 1}</span>
                            <button
                                type="button"
                                className="btn btn-ghost btn-xs text-error"
                                onClick={() => removeItem(i)}
                            >
                                <MdDelete size={14} />
                            </button>
                        </div>

                        {/* Origem */}
                        <div className="flex gap-1">
                            {([
                                { key: 'catalog' as ItemSource, label: 'Catálogo', icon: <MdStorefront size={12} /> },
                                { key: 'manual' as ItemSource, label: 'Manual', icon: null },
                            ]).map(opt => (
                                <button
                                    key={opt.key}
                                    type="button"
                                    className={`btn btn-xs flex-1 gap-1 ${state.source === opt.key ? 'btn-secondary' : 'btn-ghost'}`}
                                    onClick={() => {
                                        updateState(i, { source: opt.key, selectedCatalog: undefined, availablePrices: [] })
                                        setSearchByIndex(prev => ({ ...prev, [i]: '' }))
                                        setResultsByIndex(prev => ({ ...prev, [i]: [] }))
                                        if (opt.key === 'manual') {
                                            updateItemData(i, { catalogId: undefined })
                                        }
                                    }}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>

                        {/* Busca */}
                        {state.source !== 'manual' && (
                            <div className="relative">
                                <input
                                    type="text"
                                    className="input input-bordered input-sm w-full"
                                    placeholder="Buscar item do catálogo..."
                                    value={search}
                                    onChange={e => handleSearch(i, e.target.value)}
                                />
                                {searching && (
                                    <span className="absolute right-2 top-1/2 -translate-y-1/2 loading loading-spinner loading-xs" />
                                )}
                                {results.length > 0 && (
                                    <div className="absolute z-30 left-0 right-0 mt-1 border border-base-300 rounded-lg bg-base-100 shadow-xl max-h-48 overflow-y-auto">
                                        {results.map((r: any) => {
                                            const displayPrice = r.sellingPrice || r.price || 0
                                            return (
                                                <button
                                                    key={r.id}
                                                    type="button"
                                                    className="flex items-center justify-between w-full px-3 py-2 hover:bg-base-200 text-left border-b border-base-300/20 last:border-0"
                                                    onClick={() => selectCatalog(i, r)}
                                                >
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium truncate">{r.name}</p>
                                                        {state.source === 'catalog' && r.category && (
                                                            <p className="text-xs text-base-content/40">
                                                                {categoryLabels[r.category as keyof typeof categoryLabels] || r.category}
                                                                {r.partners?.length > 0 && ` - ${r.partners.length} parceiro(s)`}
                                                                {r.professionals?.length > 0 && ` - ${r.professionals.length} profissional(is)`}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <span className="text-sm font-bold text-secondary ml-3 flex-shrink-0">
                                                        {formatBRL(displayPrice)}
                                                    </span>
                                                </button>
                                            )
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Vinculado + Seletor de preço */}
                        {isLinked && (
                            <div className="p-2 bg-secondary/5 rounded-lg border border-secondary/20">
                                <div className="flex items-center gap-2 mb-1">
                                    <MdCheckCircle className="text-secondary flex-shrink-0" size={14} />
                                    <span className="text-xs font-medium text-secondary truncate">{item.name}</span>
                                </div>

                                {/* Seletor de origem do preço (apenas catálogo com múltiplos preços) */}
                                {state.availablePrices.length > 1 && (
                                    <div className="mt-2 space-y-1">
                                        <label className="text-[10px] font-semibold text-base-content/50 uppercase tracking-wider">
                                            Origem do preço
                                        </label>
                                        <div className="flex flex-wrap gap-1">
                                            {state.availablePrices.map((opt, pi) => {
                                                const isSelected = state.priceSource === opt.source &&
                                                    (opt.source === 'own' || (state.availablePrices.indexOf(opt) === state.availablePrices.findIndex(
                                                        p => p.source === opt.source && p.id === opt.id
                                                    )))
                                                const icon = opt.source === 'partner'
                                                    ? <MdBusiness size={10} />
                                                    : opt.source === 'professional'
                                                    ? <MdPerson size={10} />
                                                    : <MdStorefront size={10} />

                                                return (
                                                    <button
                                                        key={pi}
                                                        type="button"
                                                        className={`btn btn-xs gap-1 ${
                                                            item.unitPrice === opt.price
                                                                ? 'btn-secondary'
                                                                : 'btn-ghost border border-base-300'
                                                        }`}
                                                        onClick={() => changePriceSource(i, opt)}
                                                        title={`${opt.label}: ${formatBRL(opt.price)}`}
                                                    >
                                                        {icon}
                                                        <span className="truncate max-w-[80px]">{opt.label}</span>
                                                        <span className="font-bold">{formatBRL(opt.price)}</span>
                                                    </button>
                                                )
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Campos */}
                        <div className="space-y-2">
                            <input
                                type="text"
                                className="input input-bordered input-sm w-full"
                                value={item.name}
                                onChange={e => updateItemData(i, { name: e.target.value })}
                                placeholder="Nome do item"
                            />
                            <div className="grid grid-cols-4 gap-2">
                                <div className="form-control">
                                    <label className="label py-0"><span className="label-text text-xs">Qtd</span></label>
                                    <input
                                        type="number"
                                        className="input input-bordered input-sm"
                                        min={1}
                                        value={item.quantity || 1}
                                        onChange={e => updateItemData(i, { quantity: parseInt(e.target.value) || 1 })}
                                    />
                                </div>
                                <div className="form-control">
                                    <label className="label py-0"><span className="label-text text-xs">Preço Unit.</span></label>
                                    <input
                                        type="number"
                                        className="input input-bordered input-sm"
                                        min={0}
                                        step={0.01}
                                        value={item.unitPrice / 100 || 0}
                                        onChange={e => updateItemData(i, { unitPrice: Math.round((parseFloat(e.target.value) || 0) * 100) })}
                                    />
                                </div>
                                <div className="form-control">
                                    <label className="label py-0"><span className="label-text text-xs">Desconto</span></label>
                                    <input
                                        type="number"
                                        className="input input-bordered input-sm"
                                        min={0}
                                        step={0.01}
                                        value={(item.discount || 0) / 100}
                                        onChange={e => updateItemData(i, { discount: Math.round((parseFloat(e.target.value) || 0) * 100) })}
                                    />
                                </div>
                                <div className="form-control">
                                    <label className="label py-0"><span className="label-text text-xs">Total</span></label>
                                    <div className="input input-bordered input-sm flex items-center bg-base-200/50 text-sm font-semibold text-secondary">
                                        {formatBRL(total)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            })}

            <button type="button" className="btn btn-ghost btn-sm gap-1" onClick={addItem}>
                <MdAdd size={16} /> Adicionar Item
            </button>
        </div>
    )
}
